import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { BellIcon } from '@heroicons/react/24/outline';
import {
	ALL_FUNNEL_STATUSES,
	FUNNEL_LABELS,
	NotificationFunnelStatus,
	NotificationPreferences,
	getNotificationPreferences,
	updateNotificationPreferences,
} from '../../services/notificationPreferencesService';

/**
 * Настройки каналов и приоритетов нотификаций (TASKNEIROKLYUCH-647 hardening).
 *
 * - Toggle email / telegram (целиком канал)
 * - Для каждого канала — чекбоксы по статусам диалога и кандидата
 * - Optimistic update, revert при ошибке API
 */

export const NotificationPreferencesCard: React.FC = () => {
	const [prefs, setPrefs] = useState<NotificationPreferences | null>(null);
	const [saving, setSaving] = useState(false);

	useEffect(() => {
		getNotificationPreferences()
			.then(setPrefs)
			.catch(() => toast.error('Не удалось загрузить настройки'));
	}, []);

	const commit = async (next: NotificationPreferences) => {
		const prev = prefs;
		setPrefs(next); // optimistic
		setSaving(true);
		try {
			const saved = await updateNotificationPreferences(next);
			setPrefs(saved);
		} catch {
			setPrefs(prev);
			toast.error('Не удалось сохранить. Попробуйте ещё раз.');
		} finally {
			setSaving(false);
		}
	};

	const toggleChannel = (channel: 'emailEnabled' | 'telegramEnabled') => {
		if (!prefs) return;
		commit({ ...prefs, [channel]: !prefs[channel] });
	};

	const togglePriority = (channel: 'priorityEmail' | 'priorityTelegram', type: NotificationFunnelStatus) => {
		if (!prefs) return;
		const allowed = ALL_FUNNEL_STATUSES;
		const current = (prefs[channel] ?? [...allowed]).filter(t => allowed.includes(t));
		const next = current.includes(type) ? current.filter(t => t !== type) : [...current, type];
		commit({ ...prefs, [channel]: next });
	};

	const isPrioritySelected = (channel: 'priorityEmail' | 'priorityTelegram', type: NotificationFunnelStatus) => {
		if (!prefs) return false;
		const list = prefs[channel];
		// null/undefined = «все статусы разрешены» (см. openapi-account-notifications.yaml).
		return list == null ? true : list.includes(type);
	};

	return (
		<div className="border border-gray-200 rounded-lg p-4 bg-white">
			<div className="flex items-start gap-3">
				<BellIcon className="w-6 h-6 text-gray-600 flex-shrink-0 mt-1" />
				<div className="flex-1">
					<h3 className="font-medium text-gray-900">Настройки нотификаций</h3>
					<p className="text-sm text-gray-600 mt-1">
						Выберите каналы и статусы диалога/кандидата, по которым вы хотите получать уведомления.
					</p>

					{!prefs ? (
						<div className="mt-4 animate-spin rounded-full h-4 w-4 border-b-2 border-gray-400" />
					) : (
						<div className="mt-4 space-y-4">
							<ChannelSection
								label="Email"
								enabled={prefs.emailEnabled}
								onToggle={() => toggleChannel('emailEnabled')}
								disabled={saving}
								funnelStatuses={ALL_FUNNEL_STATUSES.map(type => ({
									type,
									checked: isPrioritySelected('priorityEmail', type),
								}))}
								onToggleType={type => togglePriority('priorityEmail', type)}
							/>
							<ChannelSection
								label="Telegram"
								enabled={prefs.telegramEnabled}
								onToggle={() => toggleChannel('telegramEnabled')}
								disabled={saving}
								funnelStatuses={ALL_FUNNEL_STATUSES.map(type => ({
									type,
									checked: isPrioritySelected('priorityTelegram', type),
								}))}
								onToggleType={type => togglePriority('priorityTelegram', type)}
							/>
						</div>
					)}
				</div>
			</div>
		</div>
	);
};

interface ChannelSectionProps {
	label: string;
	enabled: boolean;
	onToggle: () => void;
	disabled: boolean;
	funnelStatuses: { type: NotificationFunnelStatus; checked: boolean }[];
	onToggleType: (type: NotificationFunnelStatus) => void;
}

const ChannelSection: React.FC<ChannelSectionProps> = ({
	label,
	enabled,
	onToggle,
	disabled,
	funnelStatuses,
	onToggleType,
}) => (
	<div className="border-t border-gray-100 pt-3 first:border-t-0 first:pt-0">
		<label className="inline-flex items-center gap-2 cursor-pointer">
			<input
				type="checkbox"
				checked={enabled}
				onChange={onToggle}
				disabled={disabled}
				className="h-4 w-4 rounded border-gray-300 text-[#0088cc] focus:ring-[#0088cc]"
			/>
			<span className="font-medium text-gray-900">{label}</span>
		</label>
		<div className={`mt-2 ml-6 grid grid-cols-1 sm:grid-cols-2 gap-2 ${!enabled && 'opacity-50'}`}>
			{funnelStatuses.map(({ type, checked }) => (
				<label key={type} className="inline-flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
					<input
						type="checkbox"
						checked={checked}
						onChange={() => onToggleType(type)}
						disabled={disabled || !enabled}
						className="h-4 w-4 rounded border-gray-300 text-[#0088cc] focus:ring-[#0088cc]"
					/>
					{FUNNEL_LABELS[type]}
				</label>
			))}
		</div>
	</div>
);
